import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { FullContainer, InsideContainer } from './styled-components'
import { useAuthStore } from '../../zustand/authentication/use-auth-store'

import LoginLogo from './components/login/login-logo'
import LoginForm from './components/login/login-form'
import Login2FAForm from './components/login/login-2fa-form'
import LoginFooterLinks from './components/login/login-footer-links'
import ResetPassword from './components/reset-password/reset-password'
import LoginAccessDenied from './components/login/login-access-denied.jsx'

import { useLoginMutations } from './components/hooks/use-login-handlers'
import { useModalState } from './hooks/use-modal-state'
import VersionInfo from './components/version-info/index.jsx'
import LoginStoreBanner from './components/login-banner/index.jsx'
import StaticBannerComponent from '../../components/stories/molecules/static-banner/index.jsx'
import { useIntl } from 'react-intl'
import { staticBannerVariantOptions } from '../../components/stories/molecules/static-banner/constants.js'
import { modeOptions } from './constants.js'

const Login = ({ isInPopup, hideLogo }) => {
  const {
    emailVerifying,
    handleLogin2faHook,
    handleLoginHook,
    isLoading,
    loginError,
    login2faError,
    resetLoginMutations
  } = useLoginMutations()

  // Extract URL parameters
  const match = useRouteMatch()
  const isError = loginError || login2faError
  const params = match?.params
  const { formatMessage } = useIntl()

  const history = useHistory()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [staticBannerProps, setStaticBannerProps] = useState(null)

  const defineStaticBannerMessage = () => {
    let message = null
    let variant = staticBannerVariantOptions.SUCCESS

    if (isError && !isOpenLoginAccessDenied) {
      message = 'login_failed'
      variant = staticBannerVariantOptions.ERROR
    } else if (params?.mode) {
      if (params.mode === modeOptions.ACTIVATED) {
        message = 'registerForm_activationSuccessful'
      }
      if (params.mode === modeOptions.UPDATED) {
        message = 'registerForm_resetPasswordSuccessful'
      }
      if (params.mode === modeOptions.EMAIL_VERIFICATION_SUCCESS) {
        message = 'registerForm_emailVerified'
      }
      if (params.mode === modeOptions.EMAIL_VERIFICATION_FAILED) {
        message = 'registerForm_emailVerifyingWrong'
        variant = staticBannerVariantOptions.ERROR
      }
    }

    if (message) {
      return {
        message: formatMessage({ id: message }),
        variant
      }
    }

    return null
  }

  const {
    isOpenResetPassword,
    isOpenLoginAccessDenied,
    handleToggleResetPassword,
    handleToggleAccessDenied
  } = useModalState()

  const { show2FA, setShow2FA } = useAuthStore()

  const handleCloseAccessDenied = () => {
    resetLoginMutations()
    handleToggleAccessDenied()
  }

  const handleBackToLogin = () => {
    setShow2FA(undefined)
  }

  // We don't need the keypress handler anymore as forms handle submission

  const handleLogin2fa = async (data) => {
    // Get the 2FA code from the form data
    const { twoFaCode } = data

    await handleLogin2faHook({
      email,
      password,
      twoFaCode
    })
  }

  const handleLogin = async (data) => {
    // Store email for reset password component
    setEmail(data.email)
    setPassword(data.password)

    await handleLoginHook(data)
  }

  // Render helper functions for better organization
  const renderLoginForm = () => {
    if (show2FA || emailVerifying) return null

    return (
      <>
        <LoginForm
          handleLogin={handleLogin}
          isLoading={isLoading}
          isInPopup={isInPopup}
          emailVerifying={emailVerifying}
          onEmailChange={setEmail}
          defaultEmail={email}
        />
        <LoginFooterLinks
          isInPopup={isInPopup}
          openResetPassword={handleToggleResetPassword}
        />
        {!isInPopup && <VersionInfo />}
      </>
    )
  }

  const render2FAForm = () => {
    if (!show2FA) return null

    return (
      <Login2FAForm
        show2FA={show2FA}
        handleLogin={handleLogin2fa}
        handleBackToLogin={handleBackToLogin}
        error={login2faError}
        isLoading={isLoading}
        resetLoginMutations={resetLoginMutations}
      />
    )
  }

  const renderModals = () => (
    <>
      <ResetPassword
        email={email}
        isOpen={isOpenResetPassword}
        onClose={handleToggleResetPassword}
      />

      <LoginAccessDenied
        isOpen={isOpenLoginAccessDenied}
        onClose={handleCloseAccessDenied}
      />
    </>
  )

  useEffect(() => {
    if (isError?.response?.data?.error === 'User is disabled') {
      return handleToggleAccessDenied()
    }

    if (isError && show2FA) {
      setShow2FA(false)
    }
  }, [isError])

  useEffect(() => {
    if (!staticBannerProps) return

    const timer = setTimeout(() => {
      setStaticBannerProps(null)
      if (params?.mode) {
        history.replace('/')
      }
    }, 6000)

    return () => clearTimeout(timer)
  }, [staticBannerProps])

  useEffect(() => {
    const banner = defineStaticBannerMessage()
    setStaticBannerProps(banner ?? null)
  }, [params?.mode, isError, isOpenLoginAccessDenied])

  return (
    <FullContainer isInPopup={isInPopup}>
      <div>
        <LoginStoreBanner show={!isInPopup} />
        <InsideContainer isInPopup={isInPopup} maxWidth={400}>
          <LoginLogo hideLogo={hideLogo} isInPopup={isInPopup} />

          {staticBannerProps && (
            <StaticBannerComponent
              message={staticBannerProps.message}
              variant={staticBannerProps.variant}
            />
          )}

          {renderLoginForm()}

          {render2FAForm()}
        </InsideContainer>
      </div>

      {renderModals()}
    </FullContainer>
  )
}

Login.propTypes = {
  isInPopup: PropTypes.bool,
  hideLogo: PropTypes.bool
}

export default Login
